import { transformExtent } from 'ol/proj'
import type { Extent } from 'ol/extent'

/**
 * Parse the bbox string carried on a harvest WMS layer into an OpenLayers
 * extent.
 *
 * The stored value is **latitude first**: `components/layers-group/index.jsx`
 * builds it as `[bboxMiny, bboxMinx, bboxMaxy, bboxMaxx].join(',')`, and
 * `components/map/wms-layer/index.js` destructures it straight back as
 * `[minY, minX, maxY, maxX]`. OpenLayers extents are longitude first, so the
 * pairs have to be swapped on the way out. Getting this wrong silently places
 * the field in a different hemisphere rather than failing, which is why it is
 * isolated here with its own tests instead of being inlined at call sites.
 *
 * `WMSLayer.bbox` is typed `number[]` in the store but is a string in practice,
 * so both forms are accepted.
 */
export const parseWmsBbox = (
  bbox: string | number[] | undefined | null,
): Extent | null => {
  if (bbox === undefined || bbox === null) return null

  const parts = (typeof bbox === 'string' ? bbox.split(',') : bbox).map(Number)
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return null

  const [minLat, minLon, maxLat, maxLon] = parts
  if (maxLat <= minLat || maxLon <= minLon) return null

  return [minLon, minLat, maxLon, maxLat]
}

/**
 * Convert a geographic extent to the map view's projection.
 *
 * ol-cesium reads `olcs_extent` in the **view** projection (EPSG:3857 here) and
 * converts it to a Cesium rectangle itself, so handing it EPSG:4326 degrees
 * would be interpreted as metres and produce a rectangle a few metres across at
 * the equator.
 */
export const toViewExtent = (
  extent4326: Extent,
  viewProjection = 'EPSG:3857',
): Extent => transformExtent(extent4326, 'EPSG:4326', viewProjection)
